import React, { useState } from 'react';
import { matchesSkillQuery } from '../utils/skillSearch';
import './SkillMatrix.css';

const disciplineOf = level => level.discipline || 'TRA';
// Numbers inside identifiers sort as numbers: 2 before 10, D2 before D11
const byIdentifier = (a, b) => String(a.identifier).localeCompare(String(b.identifier), undefined, { numeric: true, sensitivity: 'base' });
const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'todo', label: 'Not signed off' },
  { id: 'done', label: 'Signed off' },
];

// Every skill on one page, by level, with the ones in no level at the end,
// so a coach can sign off a lot at once (e.g. a gymnast joining with
// experience from another club). Sign-offs are per apparatus, as on the
// Levels tab.
export default function SkillMatrix({ levels, skills, gymnast, apparatus, apparatusTabs, onApparatusChange, canEdit, loadingSkills, onStatusChange }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  // Groups start closed; these are the ones the coach has opened
  const [opened, setOpened] = useState({});
  const isOpen = (id) => Boolean(opened[id]);
  const toggle = (id) => setOpened(t => ({ ...t, [id]: !t[id] }));
  const tariff = (s) => (apparatus === 'DMT' ? s.dmtDifficulty : s.difficulty);
  const dd = (s) => (tariff(s) == null ? '' : Number(tariff(s)).toFixed(1));

  const apparatusLevels = levels.filter(l => disciplineOf(l) === apparatus).sort(byIdentifier);
  // A skill that's only in a level's routine (not the level's own skills)
  // counts as done once the gymnast has completed a routine with it in
  const doneRoutineIds = new Set((gymnast.routineProgress || [])
    .filter(rp => rp.status === 'COMPLETED').map(rp => rp.routineId || rp.routine?.id));
  const doneInRoutine = new Set(apparatusLevels
    .flatMap(l => (l.routines || []).filter(r => doneRoutineIds.has(r.id)))
    .flatMap(r => (r.skills || []).map(rs => rs.skillId).filter(Boolean)));
  const routineOnly = new Set();
  const recordOf = (skill) => gymnast.skillProgress
    .find(sp => (sp.skill?.id || sp.skillId) === skill.id && (sp.apparatus || 'TRA') === apparatus);
  const recorded = (skill) => recordOf(skill)?.status;
  const fromRoutine = (skill) => routineOnly.has(skill.id) && doneInRoutine.has(skill.id)
    && (!recorded(skill) || recorded(skill) === 'NOT_STARTED');
  const statusOf = (skill) => (fromRoutine(skill) ? 'COMPLETED' : recorded(skill) || 'NOT_STARTED');

  // Each skill once, under the first level it's in on this apparatus: the
  // level's own skills, then any more from its routines
  const byId = new Map(skills.map(s => [s.id, s]));
  const seen = new Set();
  const groups = apparatusLevels.map(level => {
    const own = (level.skills || []).filter(s => !seen.has(s.id) && seen.add(s.id));
    const inRoutines = (level.routines || []).flatMap(r => r.skills || [])
      .filter(rs => rs.skillId && !seen.has(rs.skillId) && seen.add(rs.skillId))
      .map(rs => { routineOnly.add(rs.skillId); return byId.get(rs.skillId) || { ...rs, id: rs.skillId }; });
    return { id: level.id, title: `${level.identifier} - ${level.name}`, skills: [...own, ...inRoutines] };
  });
  // Skills in no level, in tariff bands of 0.5
  const others = skills
    .filter(s => !seen.has(s.id) && (apparatus !== 'DMT' || s.dmtDifficulty != null))
    .sort((a, b) => Number(tariff(a) || 0) - Number(tariff(b) || 0) || a.name.localeCompare(b.name));
  const bands = new Map();
  others.forEach(s => {
    const band = tariff(s) == null ? 'none' : Math.floor(Number(tariff(s)) * 2 + 1e-9) / 2;
    if (!bands.has(band)) bands.set(band, []);
    bands.get(band).push(s);
  });
  [...bands.entries()]
    .sort(([a], [b]) => (a === 'none') - (b === 'none') || a - b)
    .forEach(([band, list]) => groups.push({
      id: `none:${band}`,
      title: band === 'none'
        ? 'Not in any level · no tariff'
        : `Not in any level · tariff ${band.toFixed(1)} to ${(band + 0.4).toFixed(1)}`,
      skills: list,
    }));

  const needle = search.trim().toLowerCase();
  // Understands ½, 1/2 and half alike, and tuck/pike/straight for (T)/(P)/(S)
  const keep = (s) => matchesSkillQuery(search, s.name, s.figNotation)
    && (filter === 'all' || (filter === 'done') === (statusOf(s) === 'COMPLETED'));
  const open = (id) => Boolean(needle) || isOpen(id);
  const visible = groups.map(g => ({ ...g, shown: g.skills.filter(keep) })).filter(g => g.shown.length);
  const total = groups.reduce((n, g) => n + g.skills.length, 0);
  const done = groups.reduce((n, g) => n + g.skills.filter(s => statusOf(s) === 'COMPLETED').length, 0);

  // Compact buttons of its own: the shared .btn grows to full width on phones
  const button = (skill, status, symbol, title) => (
    <button
      type="button"
      onClick={() => onStatusChange(skill.id, status, apparatus)}
      className={`skill-matrix__choice skill-matrix__choice--${status.toLowerCase()}`}
      title={title}
      aria-label={`${title}: ${skill.name}`}
      aria-pressed={statusOf(skill) === status}
      disabled={loadingSkills.has(skill.id)}
    >
      {symbol}
    </button>
  );
  const badge = (skill, status) => (status === 'COMPLETED'
    ? <span className="skill-matrix__badge skill-matrix__badge--completed">{fromRoutine(skill) ? '✓ Done in routine' : '✓ Signed off'}</span>
    : status === 'IN_PROGRESS' ? <span className="skill-matrix__badge skill-matrix__badge--in_progress">Working on</span> : null);

  return (
    <div className="card skill-matrix">
      <div className="card-header">
        <h4 className="card-title" style={{ margin: 0 }}>Skill matrix</h4>
        <small className="text-muted">{done} of {total} {apparatus === 'DMT' ? 'double mini' : 'trampoline'} skills signed off. Skills only in a level's routine count as done once that routine is.</small>
      </div>
      <div style={{ padding: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
        {apparatusTabs.length > 1 && (
          <select className="form-control" style={{ width: 'auto' }} aria-label="Apparatus" value={apparatus} onChange={e => onApparatusChange(e.target.value)}>
            {apparatusTabs.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        )}
        <input
          type="search"
          className="form-control"
          style={{ flex: '1 1 12rem', width: 'auto' }}
          placeholder="Find a skill"
          aria-label="Find a skill"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div role="group" aria-label="Show" className="skill-matrix__filters">
          {FILTERS.map(f => (
            <button key={f.id} type="button" className="skill-matrix__filter" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ padding: '0 0.75rem 0.75rem' }}>
        {!visible.length && <p className="text-muted">No skills match.</p>}
        {visible.map(g => (
          <section key={g.id} style={{ marginBottom: '1rem' }}>
            <h5 style={{ margin: '0.5rem 0' }}>
              <button
                type="button"
                className="btn btn-link"
                style={{ padding: 0, fontWeight: 600, color: 'inherit', textDecoration: 'none' }}
                aria-expanded={open(g.id)}
                onClick={() => toggle(g.id)}
              >
                {open(g.id) ? '▾' : '▸'} {g.title} <small className="text-muted">({g.skills.filter(s => statusOf(s) === 'COMPLETED').length}/{g.skills.length})</small>
              </button>
            </h5>
            {open(g.id) && <ul className="skill-matrix__list">
              {g.shown.map(skill => {
                const status = statusOf(skill);
                return (
                  <li key={skill.id} className={`skill-matrix__row skill-matrix__row--${status.toLowerCase()}`}>
                    <div className="skill-matrix__skill">
                      <span className="skill-matrix__name">{skill.name}</span>
                      <span className="skill-matrix__meta">
                        {dd(skill) && <span>DD {dd(skill)}</span>}
                        {badge(skill, status)}
                        {recordOf(skill)?.pending && (
                          <span className="skill-matrix__badge skill-matrix__badge--pending" title="Saved on this device. It'll be sent when there's signal.">Not sent yet</span>
                        )}
                      </span>
                    </div>
                    {canEdit && (
                      <div className="skill-matrix__choices" role="group" aria-label={`Status of ${skill.name}`}>
                        {button(skill, 'NOT_STARTED', '○', 'Not Started')}
                        {button(skill, 'IN_PROGRESS', '◐', 'In Progress')}
                        {button(skill, 'COMPLETED', '✓', 'Completed')}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>}
          </section>
        ))}
      </div>
    </div>
  );
}
